import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useAuth } from './AuthContext';

function Wizard() {
  const { token: kc_token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedContributors, setSelectedContributors] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [step, setStep] = useState(1);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Draft');
  const [scenarioTitle, setScenarioTitle] = useState('');
  const [scenarioDescription, setScenarioDescription] = useState('');
  const [scenarioSteps, setScenarioSteps] = useState([{ location: '', description: '', reference: '' }]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [generatedScript, setGeneratedScript] = useState('');
  const [usersWithRole, setUsersWithRole] = useState([]);
  const [showContributors, setShowContributors] = useState(false);
  const mockContributors = [
    { id: 'u1', username: 'amelie', email: 'amelie@example.com' },
    { id: 'u2', username: 'yassine', email: 'yassine@example.com' },
    { id: 'u3', username: 'lucie', email: 'lucie@example.com' },
    { id: 'u4', username: 'hugo', email: 'hugo@example.com' }
  ];
  const location = useLocation();
  const navigate = useNavigate();

  const predefinedTags = [
    'culture', 'histoire', 'nature', 'traditions', 'modernité',
    'gastronomie', 'enfance', 'portrait', 'art', 'architecture',
    'ville', 'rural', 'célébration', 'quotidien', 'spiritualité'
  ];

  const extractYouTubeId = (url) => {
    const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  const handleGenerateStepAI = async (index) => {
    const step = scenarioSteps[index];
    const prompt = `Rédige une description immersive pour un documentaire sur le lieu suivant : ${step.location}`;

    try {
      const res = await fetch('/generate-doc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${kc_token}`
        },
        body: JSON.stringify({ title, location: step.location, prompt })
      });
      const data = await res.json();
      const updated = [...scenarioSteps];
      updated[index].description = data.documentary || '';
      setScenarioSteps(updated);
    } catch (err) {
      console.error("Erreur IA pour une étape :", err);
    }
  };

  const toggleContributor = (username) => {
    setSelectedContributors(prev =>
      prev.includes(username)
        ? prev.filter(u => u !== username)
        : [...prev, username]
    );
  };
  const geocodeLocation = async (location) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(location)}&format=json`);
      const data = await res.json();
      if (data[0]) {
        return { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
      }
    } catch (e) {
      console.error("Erreur de géocodage :", e);
    }
    return null;
  };
  const [visibility, setVisibility] = useState('PRIVATE');
  const queryParams = new URLSearchParams(location.search);
  const edit = queryParams.get('edit') === 'true';
  const id = queryParams.get('id');

  useEffect(() => {
    if (edit && id) {
      // 🔹 Chargement du projet principal
      fetch(`/projects/${id}`)
        .then(res => res.json())
        .then(project => {
          setTitle(project.title || '');
          setDescription(project.description || '');
          setStatus(project.status || 'Draft');
          setVisibility(project.visibility || 'PRIVATE');
        });

      // 🔹 Chargement du scénario associé
      fetch(`/projects/${id}/scenarios`)
        .then(res => res.json())
        .then(scenario => {
          if (scenario && scenario.id) {
            setScenarioTitle(scenario.title || '');
            setScenarioDescription(scenario.description || '');
            try {
              const parsedSteps = JSON.parse(scenario.steps || '[]');
              setScenarioSteps(Array.isArray(parsedSteps) ? parsedSteps : []);
            } catch (error) {
              console.error("❌ Erreur parsing scénario.steps :", error);
            }
          }
        });

      // 🔹 Chargement des rôles (contributeurs)
      fetch(`/projects/${id}/roles`)
        .then(res => res.json())
        .then(roles => {
          if (Array.isArray(roles)) {
            setSelectedContributors(roles); // format: [{ username, role }]
          }
        })
        .catch(err => console.error("❌ Erreur chargement des rôles :", err));
    }
  }, [edit, id]);


  useEffect(() => {
    if (step === 2) {
      setLoadingUsers(true);

      const loadUsersAndRoles = async () => {
        try {
          const usersRes = await fetch('/keycloak/users');
          if (!usersRes.ok) throw new Error('Erreur chargement utilisateurs');
          const usersData = await usersRes.json();

          let rolesData = [];
          if (edit && id) {
            const rolesRes = await fetch(`/projects/${id}/roles`);
            if (rolesRes.ok) {
              rolesData = await rolesRes.json();
            }
          }

          const merged = usersData.map(user => {
            const matched = rolesData.find(r => r.username === user.username);
            return {
              ...user,
              role: matched?.role || ''
            };
          });

          setUsers(usersData);
          setUsersWithRole(merged);
          setSelectedContributors(rolesData);
          setLoadingUsers(false);
        } catch (error) {
          console.error("❌ Erreur chargement utilisateurs/rôles :", error);
          setLoadingUsers(false);
        }
      };

      loadUsersAndRoles();
    }
  }, [step, edit, id]);


  useEffect(() => {
    if (step === 2 && users.length > 0) {
      const merged = users.map(user => {
        const existing = selectedContributors.find(u => u.username === user.username);
        return {
          ...user,
          role: existing?.role || ''
        };
      });
      setUsersWithRole(merged);
    }
  }, [step, users, selectedContributors]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const generateScenarioFromTags = async () => {
    const prompt = `Crée un scénario captivant pour un projet documentaire intitulé "${title}" basé sur les thèmes suivants : ${selectedTags.join(', ')}.`;
    const res = await fetch('/generate-doc', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${kc_token}`
      },
      body: JSON.stringify({ title, location: description, tags: selectedTags, prompt })
    });
    const data = await res.json();
    setScenarioDescription(data.documentary || '');
  };

  const saveScenarioToProject = async (projectId) => {
    const scenarioPayload = {
      title: scenarioTitle,
      description: scenarioDescription,
      steps: JSON.stringify(scenarioSteps)
    };

    await fetch(`/projects/${projectId}/scenario`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${kc_token}`
      },
      body: JSON.stringify(scenarioPayload)
    });
  };

  const handleSaveAll = async () => {
    try {
      const projectPayload = {
        title,
        description,
        status,
        script: generatedScript || 'En attente de génération...',
        visibility
      };

      let projectId = null;

      if (edit && id) {
        await fetch(`/projects/${id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${kc_token}`
          },
          body: JSON.stringify(projectPayload)
        });
        projectId = id;
      } else {
        const response = await fetch('/projects', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${kc_token}`
          },
          body: JSON.stringify(projectPayload)
        });
        const data = await response.json();
        projectId = data.id;
      }
      await fetch(`/projects/${projectId}/roles/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${kc_token}`
        },
        body: JSON.stringify({ roles: selectedContributors })
      });
      await saveScenarioToProject(projectId);
      // 🔹 Sauvegarde des rôles
      await fetch(`/projects/${projectId}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${kc_token}`
        },
        body: JSON.stringify({ roles: selectedContributors }) // format [{ username, role }]
      });
      const refreshedRoles = await fetch(`/projects/${projectId}/roles`);
      if (refreshedRoles.ok) {
        const updatedRoles = await refreshedRoles.json();
        setSelectedContributors(updatedRoles); // force la mise à jour visuelle
      }
      navigate('/production');
    } catch (error) {
      console.error("Erreur lors de la sauvegarde :", error);
      alert("Erreur lors de l'enregistrement du projet.");
    }
  };

  return (
    <div className="container mt-4" style={{ paddingTop: '50px' }}> 
    <br/> <br/>
      <h2>🎬 Création d’un projet documentaire</h2>

      <hr />

      {step === 1 && (
        <>
          <h4>📌 Informations du projet</h4>
          <input className="form-control my-2" placeholder="Titre du projet" value={title} onChange={e => setTitle(e.target.value)} />
          <textarea style={{ height: 'auto', overflow: 'hidden' }} rows={4} className="form-control my-2" placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} />
          <div className="mb-3">
            <label className="form-label fw-semibold">🔒 Visibilité du projet</label>
            <select
              className="form-select"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}

            >
              <option value="PRIVATE">Privé (visible uniquement par l’équipe)</option>
              <option value="PUBLIC">Public (partageable avec lien)</option>
            </select>
          </div>

          <select className="form-select my-2" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="Draft">Brouillon</option>
            <option value="Active"> Actif</option>
            <option value="Clôturé">Clôturé</option>
          </select>

          <hr />
          <h4>📖 Scénario</h4>
          <input className="form-control my-2" placeholder="Titre du scénario" value={scenarioTitle} onChange={e => setScenarioTitle(e.target.value)} />
          <label>🎯 Thèmes</label>
          <div className="d-flex flex-wrap gap-2 mb-2">
            {predefinedTags.map(tag => (
              <button
                key={tag}
                className={`btn btn-sm ${selectedTags.includes(tag) ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => toggleTag(tag)}
              >
                {selectedTags.includes(tag) ? '✅ ' : '➕ '} {tag}
              </button>
            ))}
          </div>
          <button className="btn btn-outline-dark btn-sm mb-3" onClick={generateScenarioFromTags}>🤖 Générer le scénario</button>
          <textarea style={{ height: 'auto', overflow: 'hidden' }} rows={8} className="form-control mb-3" placeholder="Description du scénario" value={scenarioDescription} onChange={(e) => setScenarioDescription(e.target.value)} />

          {scenarioSteps.map((step, i) => (
            <div key={i} className="card border-0 shadow-sm mb-4 p-4 rounded-4 bg-light-subtle">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">📍 Étape {i + 1}</h5>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => {
                    const updated = [...scenarioSteps];
                    updated.splice(i, 1);
                    setScenarioSteps(updated);
                  }}
                >
                  🗑 Supprimer
                </button>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">🌍 Lieu</label>
                <input
                  className="form-control"
                  placeholder="Ex : Bab el-Foutouh, Le Caire"
                  value={step.location}
                  onChange={(e) => {
                    const updated = [...scenarioSteps];
                    updated[i].location = e.target.value;
                    setScenarioSteps(updated);
                  }}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">📜 Description</label>
                <div className="d-flex align-items-start gap-2">
                  <textarea style={{ height: 'auto', overflow: 'hidden' }} rows={7}
                    className="form-control"
                    placeholder="Décris ce lieu comme s’il faisait partie d’un documentaire..."
                    value={step.description}
                    onChange={(e) => {
                      const updated = [...scenarioSteps];
                      updated[i].description = e.target.value;
                      setScenarioSteps(updated);
                    }}
                  />
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => handleGenerateStepAI(i)}
                    title="Générer avec l'IA"
                  >
                    🤖
                  </button>
                </div>
              </div>



              {/* Aperçu YouTube si détecté */}
              {step.reference?.includes("youtube.com") && (
                <div className="mt-3">
                  <iframe
                    width="100%"
                    height="200"
                    src={`https://www.youtube.com/embed/${extractYouTubeId(step.reference)}`}
                    title="YouTube preview"
                    frameBorder="0"
                    allowFullScreen
                  />
                </div>
              )}
            </div>
          ))}

          <button className="btn btn-outline-secondary mb-3" onClick={() => setScenarioSteps([...scenarioSteps, { location: '', description: '', reference: '' }])}>
            ➕ Ajouter une étape
          </button>

          <div className="text-end">
            <button className="btn btn-primary" onClick={() => setStep(2)}>➡️ Étape suivante</button>
            <br></br>
            <br></br>
          </div>
        </>
      )}

      {step === 2 && (
  <>
    <h4 className="text-success"> Presque prêt à démarrer</h4>
    <p>Vous êtes prêt à inviter les créateurs de contenu à contribuer à ce projet.</p>

    {visibility === 'PUBLIC' ? (
      <>
        <div className="alert alert-info">
           Ce projet est <strong>public</strong> et visible par tous les membres de la plateforme.<br />
          Vous pouvez néanmoins spécifier certains membres comme <em>co-scénaristes</em> ou contributeurs privilégiés.
        </div>
        <div className="text-end mb-3">
          <button className="btn btn-outline-primary btn-sm" onClick={() => setShowContributors(prev => !prev)}>
            {showContributors ? ' Masquer la liste' : '✏️ Modifier la liste des contributeurs'}
          </button>
        </div>
      </>
    ) : null}

    {(visibility === 'PRIVATE' || showContributors) && (
      <>
        <h4 className="mb-3"> Sélection des contributeurs</h4>
        <p className="text-muted">Choisissez les membres de la communauté à inviter pour participer à ce projet.</p>

        {loadingUsers ? (
          <p>🔄 Chargement des utilisateurs…</p>
        ) : (
          <div className="row">
            {usersWithRole.map(user => (
              <div className="col-md-4 mb-3" key={user.id}>
                <div className={`card p-3 shadow-sm ${user.role ? 'border-success' : ''}`}>
                  <h6>{user.firstName} {user.lastName}</h6>
                  <p className="text-muted small">{user.email}</p>

                  <select
                    className="form-select mb-2"
                    value={user.role}
                    onChange={e => {
                      const role = e.target.value;
                      const updated = [...usersWithRole];
                      const target = updated.find(u => u.username === user.username);
                      if (target) target.role = role;
                      setUsersWithRole(updated);

                      setSelectedContributors(prev => {
                        const exists = prev.find(u => u.username === user.username);
                        if (exists) {
                          return prev.map(u => u.username === user.username ? { ...u, role } : u);
                        } else {
                          return [...prev, { username: user.username, role }];
                        }
                      });
                    }}
                  >
                    <option value="">➕ Sélectionner un rôle</option>
                    <option value="contributor"> Contributeur</option>
                    <option value="co-writer"> Co-scénariste</option>
                  </select>

                  <button
                    className={`btn btn-sm ${user.role ? 'btn-danger' : 'btn-outline-primary'}`}
                    onClick={() => {
                      const updated = usersWithRole.map(u =>
                        u.username === user.username ? { ...u, role: '' } : u
                      );
                      setUsersWithRole(updated);
                      setSelectedContributors(prev =>
                        prev.filter(u => u.username !== user.username)
                      );
                    }}
                  >
                    {user.role ? ' Retirer' : ' Inviter'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </>
    )}

    <div className="text-end mt-4">
      <button className="btn btn-secondary me-2" onClick={() => setStep(1)}> Retour</button>
      <button className="btn btn-success" onClick={handleSaveAll}>
        Sauvegarder le projet
      </button>
      <br></br>
            <br></br>
    </div>
  </>
)}

    </div>
  );
}

export default Wizard;
